<template>
  <div>
    <div class="relative w-full">

      <!-- Cart button -->
      <div class="cart-container">
        <button class="cart-btn" @click="openCartModal">
          <div class="relative">
            <img
              :src="require('@/assets/cart.svg')"
              alt="Cart"
              class="cart-icon"
            />
            <span class="absolute -bottom-2 -right-2 cart-counter">
              {{ selectedImages.length }}
            </span>
          </div>
          <span class="cart-label">View cart</span>
        </button>
      </div>

      <!-- Header -->
      <div class="text-center mt-4 mx-auto max-w-7xl px-4 sm:mt-2 sm:px-6">
        <h2 class="text-2xl tracking-tight font-extrabold text-gray-900">
          {{ name }}
        </h2>
        <h2 class="text-xl font-bold text-gray-900 whitespace-nowrap">
          {{ eventTerminology ? 'Your photos, your memories.' : '🎓 Your moment, your memory. 🎓' }}
        </h2>
        <p class="mt-2 text-gray-700">
          {{ eventTerminology ? 'Choose the event photo(s) you would like to keep.' : 'Choose the Snapshot(s) that brings your achievement to life.' }}
        </p>
        <p class="mt-2 text-gray-700">
          You can choose as many as you like!
        </p>
        <p v-if="isFreeDigital" class="mt-2 text-gray-700 font-semibold">
          Digital snapshots for this {{ eventTerminology ? 'event' : 'ceremony' }} are free — add your favourites and download them instantly.
        </p>
      </div>

      <div
        v-if="freeDownloadSuccess"
        class="mt-4 mx-auto max-w-3xl bg-green-100 text-green-800 p-4 rounded-lg"
      >
        <p>
          Your digital images are downloading. If it doesn't start automatically,
          <button
            class="font-semibold underline"
            type="button"
            @click="retryDownload"
            :disabled="freeDownloadInProgress"
          >
            click here to try again
          </button>.
        </p>
        <p v-if="lastOrderNanoId" class="mt-2">
          Collection code: <span class="font-semibold">{{ lastOrderNanoId }}</span>
        </p>
      </div>
      <div
        v-else-if="freeDownloadError"
        class="mt-4 mx-auto max-w-3xl bg-red-100 text-red-800 p-4 rounded-lg"
      >
        <p>{{ freeDownloadError }}</p>
        <button
          class="mt-2 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
          type="button"
          @click="retryDownload"
          :disabled="freeDownloadInProgress"
        >
          Try again
        </button>
      </div>
      <div
        v-else-if="freeDownloadInProgress"
        class="mt-4 mx-auto max-w-3xl bg-blue-100 text-blue-800 p-4 rounded-lg"
      >
        <p>We're preparing your download. This can take a few moments.</p>
      </div>

      <!-- Grid/Carousel View -->
      <div v-if="useNewGallery" class="w-full">
        <snapshot-selection-gallery-new
          :images="images"
          :selectedImages="selectedImages"
          :directDownloadEnabled="directDownloadEnabled"
          :downloadInProgress="downloadInProgress"
          :downloadError="downloadError"
          @toggle-image="toggleImageSelection"
          @download-image="downloadImage"
          @select-all="selectAllImages"
          @clear-all="clearAllImages"
          @view-cart="openCartModal"
        />
      </div>
      <div v-else>
        <div v-if="!showCarousel" class="mx-auto max-w-lg">
          <preview-grid
            :images="images"
            :selectedImages="selectedImages"
            @select-image="showCarouselAtIndex"
          />
        </div>
        <div v-else>
          <preview-carousel
            :images="images"
            :selectedImages="selectedImages"
            :settings="carouselSettings"
            :initial-slide="currentImageIndex"
            :directDownloadEnabled="directDownloadEnabled"
            :downloadInProgress="downloadInProgress"
            :downloadError="downloadError"
            @add-to-cart="handleAddToCart"
            @download-image="downloadImage"
            @back-to-grid="showCarousel = false"
            @view-cart="openCartModal"
          />
        </div>
      </div>
    </div>

    <!-- Cart Modal -->
    <cart-modal
      :is-open="isCartModalOpen"
      :items="selectedImages"
      :basketUrl="basketUrl"
      :is-free-digital="isFreeDigital"
      @remove="removeItem"
      @close="closeCartModal"
      @submit="goToCheckout"
      @share="shareBasket"
    />
    <div v-if="printsEnabled && !commerceEnabled" class="text-center mt-4 mx-auto max-w-lg px-4 sm:mt-2 sm:px-6">
        <p class="text-xl font-bold text-gray-900">
          Note: Award Photo Prints are only available in person on the day of your {{ eventTerminology ? 'event' : 'ceremony' }}.
          </p><p>Please look for signage of where to go to pay and collect.
          You must select your images here prior to printing.
        </p>
       
      </div>

  </div>
</template>

<script>
import { trackEvent } from "../../Analytics.vue";
import { getSnapshotConfig } from "../../snapshotConfig";
import axios from "axios";
import PreviewCarousel from "./PreviewCarousel.vue";
import CartModal from "./CartModal.vue";
import PreviewGrid from "./PreviewGrid.vue";
import SnapshotSelectionGalleryNew from "./SnapshotSelectionGalleryNew.vue";

export default {
  name: "SnapshotImageSelector",
  components: {
    PreviewCarousel,
    CartModal,
    PreviewGrid,
    SnapshotSelectionGalleryNew,
  },
  props: {
    name: String,
    images: Array,
    basketUrl: String,
    isFreeDigital: { type: Boolean, default: false },
    directDownloadEnabled: { type: Boolean, default: false },
    ceremonyId: { type: String, default: "" },
    freeDownloadSuccess: { type: Boolean, default: false },
    freeDownloadError: { type: String, default: null },
    freeDownloadInProgress: { type: Boolean, default: false },
    lastOrderNanoId: { type: String, default: "" },
  },
  data() {
    return {
      selectedImages: [],
      isCartModalOpen: false,
      carouselSettings: {
        focusOnSelect: true,
        infinite: true,
        speed: 50,
        waitForAnimate: false,
        centerMode: true,
        lazyLoad: 'ondemand',
        slidesToShow: 3,
        slidesToScroll: 1,
        swipeToSlide: true,
        swipe: true,
        draggable: true,
        dots: false,
        arrows: true,
        responsive: [
          {
            breakpoint: 1024,
            settings: {
              slidesToShow: 3,
              slidesToScroll: 1,
              infinite: true,
              dots: false,
            },
          },
          {
            breakpoint: 600,
            settings: {
              slidesToShow: 1,
              slidesToScroll: 1,
            },
          },
          {
            breakpoint: 480,
            settings: {
              focusOnSelect: true,
              infinite: true,
              centerMode: true,
              slidesToShow: 1,
              slidesToScroll: 1,
              swipe: true,
              draggable: true,
              dots: false,
              arrows: true,

            },
          },
        ],
      },
      showCarousel: false,
      currentImageIndex: 0,
      downloadInProgress: false,
      downloadError: "",
    };
  },
  created: function () {
    trackEvent(this.$route.path, {
      label: "SNAPSHOT_SELECTOR_OPENED",
      image_count: Array.isArray(this.images) ? this.images.length : 0,
    });
    if(this.$route.query.shareBasket){
      this.images.forEach((item, i) => {
        this.handleAddToCart(i)
      });
      this.isCartModalOpen = true
    }
  },
  methods: {
    async downloadImage(image) {
      if (!this.directDownloadEnabled || !this.ceremonyId || this.downloadInProgress) return;
      this.downloadError = "";
      this.downloadInProgress = true;
      try {
        let reference = image.photo_id ? { photo_id: image.photo_id } : null;
        if (!reference) {
          const path = new URL(image.src).pathname;
          const marker = `/${this.ceremonyId}/thumbnails/`;
          if (!path.includes(marker)) throw new Error("Image reference unavailable");
          reference = { file: decodeURIComponent(path.split(marker)[1]) };
        }
        const result = await axios.post(
          `${process.env.VUE_APP_SNAPSHOT_API_BASE}/download-free-image`,
          { ceremony_id: this.ceremonyId, ...reference }
        );
        if (!result.data?.url) throw new Error("Download link unavailable");
        trackEvent(this.$route.path, {
          label: "SNAPSHOT_FREE_IMAGE_DOWNLOAD",
          ceremony_id: this.ceremonyId,
        });
        window.location.href = result.data.url;
      } catch (error) {
        this.downloadError = error?.response?.data?.error ||
          "We couldn't download this photo. Please try again.";
      } finally {
        this.downloadInProgress = false;
      }
    },
    handleAddToCart(imageIndex) {
      // Grab the image from 'images' array by index
      const image = this.images[imageIndex];
      var printOnly = false;
      if (typeof this.$route.query.printOnly === undefined) {
        printOnly = false;
      }
      else {
        if (this.$route.query.printOnly == "true") {
          printOnly = true
        }
        else {
          printOnly = false
        }
      }
      // If it’s not already in selectedImages, push it
      if (!this.selectedImages.includes(image)) {
        // Initialize isDigitalSelected, isPrintSelected, etc.
        if (printOnly){
          this.$set(image, "isPrintSelected", true);
          this.$set(image, "isDigitalSelected", false);
          this.$set(image, "printQuantity", 1);

        }
        else {
          // Only allow digital
          this.$set(image, "isDigitalSelected", true);
          this.$set(image, "isPrintSelected", false);
        }
       
        this.selectedImages.push(image);
        trackEvent(this.$route.path, {
              label: "SNAPSHOT_CART_ADD_ITEM",
              img_index: imageIndex,
              type: image.isPrintSelected ? "PRINT" : "DIGITAL",
              img_src: image.src
        });
      } else {
        // Or remove if you'd like toggling behavior
        // this.selectedImages.splice(this.selectedImages.indexOf(image), 1);
      }
    },
    toggleImageSelection(imageIndex) {
      const image = this.images[imageIndex];
      if (!image) return;
      if (this.selectedImages.includes(image)) {
        this.removeItem(image);
        return;
      }
      this.handleAddToCart(imageIndex);
    },
    selectAllImages() {
      this.images.forEach((image, index) => {
        if (!this.selectedImages.includes(image)) {
          this.handleAddToCart(index);
        }
      });
    },
    clearAllImages() {
      const selected = [...this.selectedImages];
      selected.forEach((image) => this.removeItem(image));
    },
    goToCheckout(items, form, purchase) {
      this.isCartModalOpen = false;
      this.$emit("checkout", items, form, purchase)
    },
    retryDownload() {
      this.$emit('retry-free-download');
    },
    resetSelection() {
      this.selectedImages = [];
      this.closeCartModal();
    },
    shareBasket(items) {
      this.$emit('share', items)
    },
    openCartModal() {
      this.isCartModalOpen = true;
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_CART_OPENED",
        selected_count: this.selectedImages.length,
      });
    },
    closeCartModal() {
      this.isCartModalOpen = false;
    },
    removeItem(image) {
      const idx = this.selectedImages.indexOf(image);
      if (idx !== -1) {
        this.selectedImages.splice(idx, 1);
      }
      if (this.selectedImages.length === 0) {
        this.closeCartModal();
      }
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_CART_REMOVE_ITEM",
        img_index: this.images.indexOf(image),
        img_src: image.src
      });
    },
    showCarouselAtIndex(index) {
      this.currentImageIndex = index;
      this.showCarousel = true;
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_DISPLAY_CAROUSEL",
        img_index: index
      });
      
    },
  },
  computed: {
    useNewGallery() {
      return (
        String(process.env.VUE_APP_SNAPSHOT_NEW_GALLERY || "")
          .toLowerCase() === "true"
      );
    },
    snapshotConfig() {
      return getSnapshotConfig(this.$route.params.uni);
    },
    isHeadshot() {
      return this.snapshotConfig.isHeadshot;
    },
    eventTerminology() {
      return Boolean(this.snapshotConfig.eventTerminology) && !this.isHeadshot;
    },
    commerceEnabled() {
      return Boolean(this.snapshotConfig.commerce && this.snapshotConfig.commerce.enabled);
    },
    printsEnabled() {
      if (this.snapshotConfig.printsEnabled === false) return false;
      return !this.isHeadshot;
    }
  }
};
</script>

<style scoped>


.cart-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
}

.cart-btn {
  background: transparent;
  border: none;
  color: #111827;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 999px;
  cursor: pointer;
  position: relative;
  box-shadow: none;
}

.cart-icon {
  width: 3em;
  height: 3em;
}

.cart-counter {
  position: absolute;
  bottom: -8px;
  right: -8px;
  background-color: #0f766e;
  color: white;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 12px;
  min-width: 20px;
  text-align: center;
}

.cart-label {
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  display: none;
}

@media (max-width: 640px) {
  .cart-container {
    top: auto;
    right: auto;
    left: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom));
  }

  .cart-btn {
    background: #08697b;
    color: #fff;
    padding: 10px 14px;
    box-shadow: 0 10px 24px rgba(8, 105, 123, 0.25);
  }

  .cart-icon {
    width: 1.8em;
    height: 1.8em;
  }

  .cart-counter {
    bottom: -6px;
    right: -6px;
  }

  .cart-label {
    display: inline;
  }
}
/* Remove the old selected-images styles */
.selected-images {
  display: none;
}


</style>
